import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Truck, Scan, Plus, Trash2, Package, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * FLUSSO A v2 — Uscita Merce coerente per sacchi e bobine:
 *   1) Spunta i lotti/bobine che compongono una pedana fisica.
 *   2) Imposta tipo pedana (+ peso per sacchi) → "Componi pedana".
 *   3) Le pedane composte (sacchi e bobine) hanno una checkbox "Includi in DU":
 *      genera la DU solo con le pedane spuntate; le altre restano per una DU successiva.
 */
const STORE_KEY = "produx_uscita_pedane";
const readStored = () => {
    try {
        const d = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
        return { sacchi: Array.isArray(d.sacchi) ? d.sacchi : [], bobine: Array.isArray(d.bobine) ? d.bobine : [] };
    } catch { return { sacchi: [], bobine: [] }; }
};

export default function UscitaMerce() {
    const nav = useNavigate();

    // Dati magazzino
    const [lotti, setLotti] = useState([]);
    const [bobine, setBobine] = useState([]);
    const [tipiPedana, setTipiPedana] = useState([]);

    // Form DU
    const [dest, setDest] = useState("");
    const [causale, setCausale] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [scan, setScan] = useState("");
    const scanRef = useRef(null);

    // Composizione pedana SACCHI
    // selLotti: { [lotto_id]: { checked: bool, cartoni: int } }
    const [selLotti, setSelLotti] = useState({});
    const [sacchiTipo, setSacchiTipo] = useState("");
    // sacchiPesi: { [article_id]: string } — un peso per articolo univoco selezionato
    const [sacchiPesi, setSacchiPesi] = useState({});
    // Pedane sacchi composte: [{ key, include, tipo_pedana_codice, peso_by_article: {article_id: number|null}, righe: [...] }]
    const [sacchiGroups, setSacchiGroups] = useState(() => readStored().sacchi);

    // Composizione pedana BOBINE
    const [selBobine, setSelBobine] = useState({});
    const [bobTipo, setBobTipo] = useState("");
    // Pedane bobine composte: [{ key, include, tipo_pedana_codice, bobine_ids }]
    const [bobGroups, setBobGroups] = useState(() => readStored().bobine);

    useEffect(() => {
        localStorage.setItem(STORE_KEY, JSON.stringify({ sacchi: sacchiGroups, bobine: bobGroups }));
    }, [sacchiGroups, bobGroups]);

    const load = async () => {
        try {
            const [l, b, tp] = await Promise.all([
                api.get("/lotti?disponibili=true"),
                api.get("/bobine?stato=magazzino"),
                api.get("/tipi-pedana/").catch(() => ({ data: [] })),
            ]);
            const lt = (l.data || []).filter(x => (x.cartoni_disponibili || 0) > 0);
            const bb = b.data || [];
            setLotti(lt);
            setBobine(bb);
            // Scarta dalle pedane salvate lotti/bobine non più disponibili (es. già spediti altrove)
            const lIds = new Set(lt.map(x => x.id));
            const bIds = new Set(bb.map(x => x.id));
            setSacchiGroups(p => p.map(g => ({ ...g, righe: (g.righe || []).filter(r => lIds.has(r.lotto_id)) })).filter(g => g.righe.length));
            setBobGroups(p => p.map(g => ({ ...g, bobine_ids: (g.bobine_ids || []).filter(id => bIds.has(id)) })).filter(g => g.bobine_ids.length));
            setTipiPedana(tp.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); scanRef.current?.focus(); }, []);

    // ====== Lotti già impegnati in pedane sacchi composte ======
    const prelevatoDaLotto = (lid) => sacchiGroups.reduce((s, g) =>
        s + (g.righe || []).filter(r => r.lotto_id === lid).reduce((a, r) => a + (parseInt(r.cartoni) || 0), 0), 0);
    const disponibileLive = (l) => (l.cartoni_disponibili || 0) - prelevatoDaLotto(l.id);

    // ====== Bobine già impegnate ======
    const bobineAssegnate = useMemo(() => {
        const s = new Set();
        bobGroups.forEach(g => (g.bobine_ids || []).forEach(id => s.add(id)));
        return s;
    }, [bobGroups]);

    // ====== Scanner ======
    const onScan = (e) => {
        if (e.key !== "Enter") return;
        const code = scan.trim().toUpperCase();
        setScan("");
        if (!code) return;
        if (code.startsWith("BOB")) {
            const bob = bobine.find(b => (b.numero || "").toUpperCase() === code);
            if (!bob) { toast.error(`Bobina ${code} non trovata`); return; }
            if (bobineAssegnate.has(bob.id) || selBobine[bob.id]) { toast.info(`Bobina ${code} già selezionata`); return; }
            setSelBobine(p => ({ ...p, [bob.id]: true }));
            toast.success(`Bobina ${code} spuntata`);
            return;
        }
        if (code.startsWith("LT")) {
            const lot = lotti.find(l => (l.numero || "").toUpperCase() === code);
            if (!lot) { toast.error(`Lotto ${code} non disponibile`); return; }
            const avail = disponibileLive(lot);
            if (avail <= 0) { toast.info(`Lotto ${code} esaurito`); return; }
            setSelLotti(p => ({ ...p, [lot.id]: { checked: true, cartoni: p[lot.id]?.cartoni ?? avail } }));
            toast.success(`Lotto ${code} spuntato`);
            return;
        }
        toast.error(`Codice ${code} non riconosciuto (atteso LT-* o BOB-*)`);
    };

    // ====== Composizione pedana SACCHI ======
    const toggleLotto = (l, checked) => {
        setSelLotti(prev => {
            const cur = prev[l.id] || {};
            if (!checked) { const n = { ...prev }; delete n[l.id]; return n; }
            return { ...prev, [l.id]: { checked: true, cartoni: cur.cartoni ?? disponibileLive(l) } };
        });
    };
    const setLottoCart = (l, v) => {
        const max = disponibileLive(l);
        const n = Math.min(Math.max(0, parseInt(v) || 0), max);
        setSelLotti(prev => ({ ...prev, [l.id]: { checked: true, cartoni: n } }));
    };

    const composeSacchi = () => {
        const righe = [];
        for (const l of lotti) {
            const s = selLotti[l.id];
            if (!s?.checked) continue;
            const n = parseInt(s.cartoni) || 0;
            if (n <= 0) continue;
            righe.push({
                lotto_id: l.id, lotto_numero: l.numero, codice: l.codice,
                descrizione: l.descrizione, article_id: l.article_id, cartoni: n,
            });
        }
        if (righe.length === 0) { toast.error("Spunta almeno un lotto con cartoni > 0"); return; }
        if (!sacchiTipo) { toast.error("Scegli il tipo pedana"); return; }
        // Costruisci peso_by_article dal dict sacchiPesi (uno per articolo univoco)
        const peso_by_article = {};
        const uniqArt = Array.from(new Set(righe.map(r => r.article_id)));
        for (const aid of uniqArt) {
            const v = sacchiPesi[aid];
            peso_by_article[aid] = (v !== undefined && v !== "" && !isNaN(parseFloat(v))) ? parseFloat(v) : null;
        }
        setSacchiGroups(prev => [...prev, {
            key: `s-${Date.now()}`,
            include: true,
            tipo_pedana_codice: sacchiTipo,
            peso_by_article,
            righe,
        }]);
        setSelLotti({}); setSacchiTipo(""); setSacchiPesi({});
        const totCart = righe.reduce((a, r) => a + r.cartoni, 0);
        toast.success(`Pedana sacchi composta: ${righe.length} lotto/i · ${totCart} cart.${uniqArt.length > 1 ? ` · ${uniqArt.length} articoli` : ""}`);
    };

    const rmSacchiGroup = (key) => setSacchiGroups(p => p.filter(g => g.key !== key));
    const toggleSacchiInclude = (key, v) => setSacchiGroups(p => p.map(g => g.key === key ? { ...g, include: v } : g));
    const setSacchiGroupMeta = (key, patch) => setSacchiGroups(p => p.map(g => g.key === key ? { ...g, ...patch } : g));

    // ====== Composizione pedana BOBINE ======
    const composeBobine = () => {
        const bids = Object.entries(selBobine).filter(([, v]) => v).map(([k]) => k).filter(id => !bobineAssegnate.has(id));
        if (bids.length === 0) { toast.error("Spunta almeno una bobina"); return; }
        if (!bobTipo) { toast.error("Scegli il tipo pedana"); return; }
        setBobGroups(prev => [...prev, {
            key: `b-${Date.now()}`,
            include: true,
            tipo_pedana_codice: bobTipo,
            bobine_ids: bids,
        }]);
        setSelBobine({}); setBobTipo("");
        toast.success(`Pedana bobine composta con ${bids.length} bobina/e`);
    };
    const rmBobGroup = (key) => setBobGroups(p => p.filter(g => g.key !== key));
    const toggleBobInclude = (key, v) => setBobGroups(p => p.map(g => g.key === key ? { ...g, include: v } : g));
    const groupPesoKg = (g) => (g.bobine_ids || []).reduce((s, id) => s + (parseFloat((bobine.find(b => b.id === id) || {}).kg) || 0), 0);

    // ====== Submit DU (solo pedane con include=true) ======
    const submit = async () => {
        if (!dest.trim()) { toast.error("Destinatario obbligatorio"); return; }
        const activeSacchi = sacchiGroups.filter(g => g.include);
        const activeBob = bobGroups.filter(g => g.include);
        if (activeSacchi.length === 0 && activeBob.length === 0) {
            toast.error("Spunta almeno una pedana composta da includere in DU"); return;
        }
        // Costruisci lotti_prelievi: peso assegnato al PRIMO lotto di ciascun articolo in ogni pedana
        const lotti_prelievi = [];
        for (const g of activeSacchi) {
            const pbya = g.peso_by_article || {};
            const pesoAssegnatoArt = {};  // article_id -> bool
            for (const r of g.righe) {
                const pesoArt = pbya[r.article_id];
                const dai = !pesoAssegnatoArt[r.article_id] && pesoArt != null ? pesoArt : null;
                lotti_prelievi.push({
                    lotto_id: r.lotto_id,
                    cartoni: r.cartoni,
                    pedana_gruppo: g.key,
                    tipo_pedana_codice: g.tipo_pedana_codice,
                    peso_kg: dai,
                });
                if (pesoArt != null) pesoAssegnatoArt[r.article_id] = true;
            }
        }
        const bobine_pedane = activeBob.map(g => ({ bobine_ids: g.bobine_ids, tipo_pedana_codice: g.tipo_pedana_codice }));
        setBusy(true);
        try {
            const body = {
                pedane_ids: [], prelievi: [],
                lotti_prelievi, bobine_pedane,
                destinatario: dest, causale: causale || null, note: note || null,
            };
            const { data } = await api.post("/uscita", body);
            toast.success(`DU ${data.numero} generata`);
            if (data.id) window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uscita/${data.id}/pdf`, "_blank");
            // Rimuovi dalla sessione SOLO le pedane consumate; le altre restano in attesa
            setSacchiGroups(p => p.filter(g => !g.include));
            setBobGroups(p => p.filter(g => !g.include));
            setDest(""); setCausale(""); setNote("");
            await load();  // ricarica disponibilità aggiornate
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const totSacchiPend = sacchiGroups.filter(g => g.include).length;
    const totBobPend = bobGroups.filter(g => g.include).length;

    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-red-400/80">USCITA MERCE</div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Uscita Merce & Distinta</h1>
                <p className="mt-2 text-slate-400 text-sm max-w-3xl">
                    Spunta <b>lotti</b> e <b>bobine</b> dalle liste sotto, imposta il tipo pedana e usa <b>"Componi pedana"</b>. Le pedane composte restano in sessione: metti il <b>check "Includi in DU"</b> solo su quelle che vuoi spedire ora — le altre resteranno per una DU successiva.
                </p>
            </div>

            <Card className="card p-5 grid md:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs text-slate-400">Destinatario *</Label>
                    <Input data-testid="input-destinatario" value={dest} onChange={e => setDest(e.target.value)}
                        placeholder="Cliente / Cantiere / Ditta"
                        className="bg-slate-950/50 border-slate-800 mt-1" />
                </div>
                <div>
                    <Label className="text-xs text-slate-400">Causale</Label>
                    <Input data-testid="input-causale" value={causale} onChange={e => setCausale(e.target.value)}
                        placeholder="Vendita, reso, campionatura…"
                        className="bg-slate-950/50 border-slate-800 mt-1" />
                </div>
            </Card>

            <Card className="card p-4 border-amber-500/30 bg-amber-500/5">
                <Label className="text-xs text-amber-300 tracking-widest uppercase flex items-center gap-2 mb-2">
                    <Scan size={14} /> Scansiona lotto o bobina
                </Label>
                <Input ref={scanRef} data-testid="input-scan-uscita" value={scan}
                    onChange={e => setScan(e.target.value)} onKeyDown={onScan}
                    placeholder="LT-2026-0012V  oppure  BOB-2026-0005"
                    className="bg-slate-950/60 border-amber-500/40 h-12 text-lg font-mono tracking-wider" />
                <div className="text-[10px] text-slate-500 mt-1">
                    <span className="text-emerald-300 font-mono">LT-*</span> spunta il lotto · <span className="text-cyan-300 font-mono">BOB-*</span> spunta la bobina
                </div>
            </Card>

            {/* ===== LOTTI DISPONIBILI (SACCHI) ===== */}
            <Card className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2 flex-wrap">
                    <Package size={16} className="text-emerald-400" />
                    <h2 className="font-display font-bold text-lg">Lotti dalla produzione (Saldatura)</h2>
                    <span className="text-[10px] text-slate-500 ml-2">Disponibilità in tempo reale (sottrae quello che hai già inserito nelle pedane composte sotto)</span>
                    <div className="ml-auto flex items-center gap-2 flex-wrap">
                        {/* Peso per articolo: 1 input se un solo articolo selezionato, N se misti */}
                        {(() => {
                            const uniqArtSel = Array.from(new Set(lotti.filter(l => selLotti[l.id]?.checked).map(l => l.article_id)));
                            if (uniqArtSel.length === 0) {
                                return <Input disabled placeholder="Peso (kg)" className="bg-slate-950/60 border-slate-800 h-9 w-32 text-sm opacity-50" />;
                            }
                            return uniqArtSel.map(aid => {
                                const cod = (lotti.find(l => l.article_id === aid) || {}).codice || "—";
                                return (
                                    <Input key={aid} type="number" step="0.1"
                                        data-testid={`input-sacchi-peso-${cod}`}
                                        value={sacchiPesi[aid] || ""}
                                        onChange={e => setSacchiPesi(p => ({ ...p, [aid]: e.target.value }))}
                                        placeholder={uniqArtSel.length > 1 ? `Peso ${cod} (kg)` : "Peso (kg)"}
                                        className="bg-slate-950/60 border-slate-800 h-9 w-36 text-sm" />
                                );
                            });
                        })()}
                        <select data-testid="select-sacchi-tipo" value={sacchiTipo} onChange={e => setSacchiTipo(e.target.value)}
                            className="bg-slate-950/60 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-100">
                            <option value="">— Tipo pedana —</option>
                            {tipiPedana.map(tp => <option key={tp.id} value={tp.codice}>{tp.codice}{tp.descrizione ? ` · ${tp.descrizione}` : ""}</option>)}
                        </select>
                        <Button size="sm" onClick={composeSacchi} data-testid="btn-componi-pedana-sacchi"
                            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-9 gap-2">
                            <Plus size={14} /> Componi pedana
                        </Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 w-10"></th>
                                <th className="text-left px-5 py-3">Lotto</th>
                                <th className="text-left px-5 py-3">Articolo</th>
                                <th className="text-right px-5 py-3">Disponibili</th>
                                <th className="text-right px-5 py-3">Cartoni da prelevare</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-lotti-disponibili">
                            {lotti.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500 text-sm">Nessun lotto disponibile</td></tr>}
                            {lotti.map(l => {
                                const live = disponibileLive(l);
                                const esaurito = live <= 0;
                                const checked = !!selLotti[l.id]?.checked;
                                const curN = selLotti[l.id]?.cartoni ?? live;
                                return (
                                    <tr key={l.id} className={`border-b border-slate-800/60 ${esaurito && !checked ? "opacity-40" : "hover:bg-slate-800/30"}`} data-testid={`row-lotto-${l.numero}`}>
                                        <td className="px-5 py-3">
                                            <input type="checkbox" disabled={esaurito && !checked}
                                                checked={checked}
                                                onChange={e => toggleLotto(l, e.target.checked)}
                                                data-testid={`chk-lotto-${l.numero}`}
                                                className="w-4 h-4 accent-emerald-500 disabled:opacity-30" />
                                        </td>
                                        <td className="px-5 py-3 doc-code text-xs text-amber-300">{l.numero}</td>
                                        <td className="px-5 py-3">
                                            <span className="font-mono text-xs text-slate-200 mr-2">{l.codice}</span>
                                            <span className="text-slate-400 text-xs">{l.descrizione}</span>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <span className={`font-mono font-bold ${esaurito ? "text-slate-500" : "text-emerald-300"}`} data-testid={`disp-${l.numero}`}>{live}</span>
                                            {prelevatoDaLotto(l.id) > 0 && (
                                                <div className="text-[9px] text-slate-500 font-mono mt-0.5">tot: {l.cartoni_disponibili} · in pedana: {prelevatoDaLotto(l.id)}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <Input type="number" min="0" max={live + (checked ? 0 : 0)} disabled={!checked}
                                                data-testid={`cart-lotto-${l.numero}`}
                                                value={checked ? curN : ""}
                                                onChange={e => setLottoCart(l, e.target.value)}
                                                placeholder={checked ? "" : "—"}
                                                className="bg-slate-950/60 border-slate-800 text-xs text-right font-mono h-8 w-24 ml-auto disabled:opacity-30" />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* ===== PEDANE SACCHI COMPOSTE ===== */}
            {sacchiGroups.length > 0 && (
                <Card className="card overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                        <Layers size={16} className="text-emerald-400" />
                        <h2 className="font-display font-bold text-lg">Pedane di sacchi composte ({sacchiGroups.length})</h2>
                        <span className="text-[10px] text-slate-500 ml-2">Spunta <b>Includi in DU</b> per spedire ora — le altre restano in attesa</span>
                        <Badge className="ml-auto badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">{totSacchiPend} in DU</Badge>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-center px-5 py-3 w-20">Includi in DU</th>
                                <th className="text-left px-5 py-3">#</th>
                                <th className="text-left px-5 py-3">Tipo pedana</th>
                                <th className="text-left px-5 py-3">Lotti e cartoni</th>
                                <th className="text-right px-5 py-3">Tot cart.</th>
                                <th className="text-right px-5 py-3">Peso (kg)</th>
                                <th className="text-right px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-sacchi-groups">
                            {sacchiGroups.map((g, i) => {
                                const totCart = g.righe.reduce((s, r) => s + (parseInt(r.cartoni) || 0), 0);
                                return (
                                    <tr key={g.key} className={`border-b border-slate-800/60 ${g.include ? "" : "opacity-50"}`}>
                                        <td className="px-5 py-3 text-center">
                                            <input type="checkbox" checked={!!g.include}
                                                onChange={e => toggleSacchiInclude(g.key, e.target.checked)}
                                                data-testid={`chk-sacchi-grp-${i}`}
                                                className="w-5 h-5 accent-emerald-500" />
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-400">#{i + 1}</td>
                                        <td className="px-5 py-3">
                                            <select value={g.tipo_pedana_codice || ""} data-testid={`sacchi-grp-tipo-${i}`}
                                                onChange={e => setSacchiGroupMeta(g.key, { tipo_pedana_codice: e.target.value })}
                                                className="bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-100">
                                                {tipiPedana.map(tp => <option key={tp.id} value={tp.codice}>{tp.codice}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {g.righe.map((r, j) => (
                                                    <span key={j} className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-200">
                                                        <span className="text-amber-300">{r.lotto_numero}</span>
                                                        <span className="text-slate-400"> · {r.codice} × </span>
                                                        <b className="text-emerald-300">{r.cartoni}</b>
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono font-bold text-emerald-300">{totCart}</td>
                                        <td className="px-5 py-3 text-right">
                                            {(() => {
                                                const uniqArts = Array.from(new Set(g.righe.map(r => r.article_id)));
                                                const multi = uniqArts.length > 1;
                                                return (
                                                    <div className="flex flex-col gap-1 items-end">
                                                        {uniqArts.map(aid => {
                                                            const cod = (g.righe.find(r => r.article_id === aid) || {}).codice || "—";
                                                            const val = (g.peso_by_article || {})[aid];
                                                            return (
                                                                <div key={aid} className="flex items-center gap-1">
                                                                    {multi && <span className="text-[9px] font-mono text-slate-500 min-w-[70px] text-right">{cod}</span>}
                                                                    <Input type="number" step="0.1"
                                                                        value={val != null ? val : ""}
                                                                        onChange={e => setSacchiGroupMeta(g.key, {
                                                                            peso_by_article: {
                                                                                ...(g.peso_by_article || {}),
                                                                                [aid]: e.target.value ? parseFloat(e.target.value) : null
                                                                            }
                                                                        })}
                                                                        data-testid={`sacchi-grp-peso-${i}-${cod}`} placeholder="kg"
                                                                        className="bg-slate-950/60 border-slate-800 text-xs text-right font-mono h-8 w-24" />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <button onClick={() => rmSacchiGroup(g.key)} data-testid={`btn-rm-sacchi-${i}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            {/* ===== BOBINE DISPONIBILI ===== */}
            {bobine.length > 0 && (
                <Card className="card overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2 flex-wrap">
                        <Truck size={16} className="text-cyan-400" />
                        <h2 className="font-display font-bold text-lg">Bobine estrusione a magazzino</h2>
                        <span className="text-[10px] text-slate-500 ml-2">Spunta bobine · scegli tipo pedana · "Componi pedana"</span>
                        <div className="ml-auto flex items-center gap-2">
                            <select data-testid="select-tipo-pedana-nuova"
                                value={bobTipo} onChange={e => setBobTipo(e.target.value)}
                                className="bg-slate-950/60 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-100">
                                <option value="">— Tipo pedana —</option>
                                {tipiPedana.map(tp => <option key={tp.id} value={tp.codice}>{tp.codice}{tp.descrizione ? ` · ${tp.descrizione}` : ""}</option>)}
                            </select>
                            <Button size="sm" onClick={composeBobine} data-testid="btn-componi-pedana"
                                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 h-9 gap-2">
                                <Plus size={14} /> Componi pedana
                            </Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                                <tr>
                                    <th className="text-left px-5 py-3 w-10"></th>
                                    <th className="text-left px-5 py-3">Bobina</th>
                                    <th className="text-left px-5 py-3">Articolo</th>
                                    <th className="text-left px-5 py-3">Cliente</th>
                                    <th className="text-right px-5 py-3">Kg</th>
                                    <th className="text-left px-5 py-3">Macch.</th>
                                    <th className="text-left px-5 py-3">In pedana</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bobine.map(b => {
                                    const assegnato = bobineAssegnate.has(b.id);
                                    const grpOf = bobGroups.find(g => g.bobine_ids.includes(b.id));
                                    return (
                                        <tr key={b.id} className={`border-b border-slate-800/60 ${assegnato ? "opacity-50" : "hover:bg-slate-800/30"}`} data-testid={`row-bobina-uscita-${b.numero}`}>
                                            <td className="px-5 py-3">
                                                <input type="checkbox" disabled={assegnato}
                                                    checked={!!selBobine[b.id]}
                                                    onChange={e => setSelBobine({ ...selBobine, [b.id]: e.target.checked })}
                                                    data-testid={`chk-bobina-${b.numero}`}
                                                    className="w-4 h-4 accent-cyan-500 disabled:opacity-30" />
                                            </td>
                                            <td className="px-5 py-3 doc-code text-xs">{b.numero}</td>
                                            <td className="px-5 py-3">
                                                <span className="font-mono text-xs text-amber-300 mr-2">{b.articolo_codice}</span>
                                                <span className="text-slate-300 text-xs">{b.larghezza_cm}×{b.spessore_my}μ {b.colore_nome}</span>
                                            </td>
                                            <td className="px-5 py-3 text-xs text-slate-400">{b.cliente_nome || "—"}</td>
                                            <td className="px-5 py-3 text-right font-mono font-bold text-cyan-300">{b.kg}</td>
                                            <td className="px-5 py-3 text-xs font-mono text-slate-400">{b.macchina_codice}</td>
                                            <td className="px-5 py-3 text-xs">
                                                {grpOf ? <Badge className="badge-pill bg-cyan-500/15 text-cyan-300 border-cyan-500/40">{grpOf.tipo_pedana_codice}</Badge> : <span className="text-slate-600">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* ===== PEDANE BOBINE COMPOSTE ===== */}
            {bobGroups.length > 0 && (
                <Card className="card overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                        <Package size={16} className="text-cyan-400" />
                        <h2 className="font-display font-bold text-lg">Pedane di bobine composte ({bobGroups.length})</h2>
                        <span className="text-[10px] text-slate-500 ml-2">Peso = somma kg bobine</span>
                        <Badge className="ml-auto badge-pill bg-cyan-500/15 text-cyan-300 border-cyan-500/40">{totBobPend} in DU</Badge>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-center px-5 py-3 w-20">Includi in DU</th>
                                <th className="text-left px-5 py-3">#</th>
                                <th className="text-left px-5 py-3">Tipo pedana</th>
                                <th className="text-left px-5 py-3">Bobine</th>
                                <th className="text-right px-5 py-3">N colli</th>
                                <th className="text-right px-5 py-3">Peso (kg)</th>
                                <th className="text-right px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-bob-groups">
                            {bobGroups.map((g, i) => {
                                const bobs = g.bobine_ids.map(id => bobine.find(b => b.id === id)).filter(Boolean);
                                return (
                                    <tr key={g.key} className={`border-b border-slate-800/60 ${g.include ? "" : "opacity-50"}`}>
                                        <td className="px-5 py-3 text-center">
                                            <input type="checkbox" checked={!!g.include}
                                                onChange={e => toggleBobInclude(g.key, e.target.checked)}
                                                data-testid={`chk-bob-grp-${i}`}
                                                className="w-5 h-5 accent-cyan-500" />
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-400">#{i + 1}</td>
                                        <td className="px-5 py-3">
                                            <Badge className="badge-pill bg-cyan-500/15 text-cyan-300 border-cyan-500/40">{g.tipo_pedana_codice || "—"}</Badge>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {bobs.map(b => <span key={b.id} className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-200">{b.numero} · {b.kg}kg</span>)}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono font-bold text-slate-100">{bobs.length}</td>
                                        <td className="px-5 py-3 text-right font-mono font-bold text-cyan-300">{groupPesoKg(g).toFixed(1)}</td>
                                        <td className="px-5 py-3 text-right">
                                            <button onClick={() => rmBobGroup(g.key)} data-testid={`btn-rm-group-${i}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            <Card className="card p-5">
                <Label className="text-xs text-slate-400">Note</Label>
                <Textarea data-testid="input-uscita-note" value={note} onChange={e => setNote(e.target.value)}
                    className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
            </Card>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-500 font-mono">
                    Pedane in DU: <b className="text-emerald-300">{totSacchiPend} sacchi</b> + <b className="text-cyan-300">{totBobPend} bobine</b>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => nav("/documenti")} className="bg-transparent border-slate-700 text-slate-300 h-11">Vai ai documenti</Button>
                    <Button onClick={submit} disabled={busy || (totSacchiPend === 0 && totBobPend === 0) || !dest.trim()}
                        data-testid="button-submit-uscita"
                        className="bg-red-500 hover:bg-red-400 text-slate-950 font-semibold px-6 h-11 gap-2">
                        <Truck size={16} /> {busy ? "Attendere…" : "Genera DU"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
